import { useState, type KeyboardEvent } from "react";
import { VIDEO_TAGS_TOTAL_MAX } from "@vedaunion/shared";

/**
 * Chip-style tags input. Editor types a tag, presses Enter or comma to
 * commit. Pasting a comma-separated string adds them all. Live total-length
 * counter (YouTube caps tags at 500 chars across the whole comma-joined
 * list).
 */
export function TagsInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState("");

  const totalLength = value.join(",").length;
  const remaining = VIDEO_TAGS_TOTAL_MAX - totalLength;
  const overflow = remaining < 0;

  const commit = (raw: string) => {
    const cleaned = raw
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0 && t.length <= 100);
    if (cleaned.length === 0) return;
    const merged = [...value];
    for (const t of cleaned) {
      if (!merged.includes(t)) merged.push(t);
    }
    onChange(merged);
    setDraft("");
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (draft.trim()) commit(draft);
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="yt-tags">
      <div className="yt-tags-pills" aria-label={ariaLabel}>
        {value.map((tag) => (
          <span key={tag} className="yt-tag-pill">
            <span>{tag}</span>
            <button
              type="button"
              className="yt-tag-remove"
              onClick={() => onChange(value.filter((t) => t !== tag))}
              aria-label={`Remove tag ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          className="yt-tags-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (text.includes(",")) {
              e.preventDefault();
              commit(draft + text);
            }
          }}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
          placeholder={value.length === 0 ? "Add tags…" : ""}
          aria-label={ariaLabel ?? "Add a tag"}
        />
      </div>
      <p
        className={`yt-tags-counter tabular ${
          overflow
            ? "is-over"
            : remaining < VIDEO_TAGS_TOTAL_MAX * 0.1
              ? "is-warn"
              : ""
        }`}
      >
        {totalLength} / {VIDEO_TAGS_TOTAL_MAX} characters
      </p>
    </div>
  );
}
